/**
 * Onboarding: default target market + results-language.
 *
 * Captures the SET-ONCE audit defaults (target market + results language) that
 * every new audit inherits and that stay editable later in Settings. Both
 * persist via the preferences endpoint (see Onboarding.handleSubmit), not the
 * onboarding company payload.
 */

import { type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { MARKET_OPTIONS } from "@vardic/shared/types.ts";
import type { Market } from "@vardic/shared/types.ts";
import type { AppLanguage } from "@/utils/language-preference";

interface OnboardingRegionSectionProps {
  defaultMarket: Market;
  reportLanguage: AppLanguage;
  onChangeDefaultMarket: (market: Market) => void;
  onChangeReportLanguage: (language: AppLanguage) => void;
}

const fieldClassName =
  "mt-2 w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-4 py-3 text-sm text-[var(--foreground)] shadow-sm transition focus:border-[var(--ring)] focus:outline-none focus:ring-4 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)]";

export function OnboardingRegionSection({
  defaultMarket,
  reportLanguage,
  onChangeDefaultMarket,
  onChangeReportLanguage,
}: OnboardingRegionSectionProps) {
  const { t } = useTranslation(["onboarding"]);

  const handleMarket = (e: ChangeEvent<HTMLSelectElement>) => {
    onChangeDefaultMarket(e.target.value as Market);
  };

  const handleLanguage = (e: ChangeEvent<HTMLSelectElement>) => {
    onChangeReportLanguage(e.target.value === "en" ? "en" : "pl");
  };

  return (
    <div
      className="border-t border-[var(--border)] pt-5"
      data-test="onboarding-region-section"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--primary)]">
        {t("onboarding:region.eyebrow")}
      </p>
      <p className="mt-2 text-sm leading-6 text-[var(--muted-foreground)]">
        {t("onboarding:region.hint")}
      </p>

      <div className="mt-3">
        <label
          htmlFor="onboarding-market"
          className="block text-sm font-semibold text-[var(--foreground)]"
        >
          {t("onboarding:region.market.label")}
        </label>
        <select
          id="onboarding-market"
          data-test="onboarding-market"
          value={defaultMarket}
          onChange={handleMarket}
          className={fieldClassName}
          aria-label={t("onboarding:region.market.label")}
        >
          {MARKET_OPTIONS.map((market) => (
            <option key={market} value={market}>
              {market}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-3">
        <label
          htmlFor="onboarding-report-language"
          className="block text-sm font-semibold text-[var(--foreground)]"
        >
          {t("onboarding:region.language.label")}
        </label>
        <select
          id="onboarding-report-language"
          data-test="onboarding-report-language"
          value={reportLanguage}
          onChange={handleLanguage}
          className={fieldClassName}
          aria-label={t("onboarding:region.language.label")}
        >
          <option value="pl">{t("onboarding:region.language.pl")}</option>
          <option value="en">{t("onboarding:region.language.en")}</option>
        </select>
      </div>
    </div>
  );
}
